import {defineStore} from 'pinia';
import redirect_error_page from "@/util/redirect_error_page"
import { useVersionStore } from './version_store';
import apiRequest from '@/util/api-request';
import { AxiosRequestConfig } from 'axios';


interface IQuestionSet{
    id: number;
    title: string;
    textbook_page_range: string;
}

interface IMissions{
    uuid: string;
    mission_kind: number;
    question_set: IQuestionSet;
    question_ids: Array<number>;
}

interface ISubjectFieldMissions{
    subject_field: string;
    missions: IMissions[];
    reloadable: boolean;
}

interface IMission{
    generated_at: string;
    subject_field_missions:ISubjectFieldMissions[];
}

interface IRecommendedSubject{
    id: number;
    name: string;
}

interface ILatestQuestionSetHistories{
    id: number;
    question_set_id: number;
    study_start_datetime: string;
    study_end_datetime: string;
    number_of_questions: number;
    number_of_correct_answers: number;
    correct_answer_rate: number;
    number_of_stars: number;
}

interface IQuestionSetProgresses{
    id:number;
    question_ids:Array<number>;
    done_correctly_question_ids:Array<number>;
}

interface IMissionCard {
    mission_list: IMission;
    recommend_list: IRecommendedSubject[];
    latest_question_set_histories: ILatestQuestionSetHistories[];
    question_set_ids: Array<number>;
    current_Mission: string;
    carousel_subject: Array<any>;
    question_set_progresses: IQuestionSetProgresses[];
    card_Id: string;
}

const useTopStore = defineStore('topStore', {
    // state
    state: (): IMissionCard => {
        return {
            mission_list: {
                generated_at: null,
                subject_field_missions: []
            },
            recommend_list: [],
            latest_question_set_histories: [],
            question_set_ids: [],
            current_Mission: "",
            carousel_subject: [],
            question_set_progresses: [],
            card_Id: ""
        }
    },
    // getters
    getters: {
        missions: (state) => {
            return state.mission_list
        },
        recommend: (state) => {
            return state.recommend_list
        },
        latestQuestionSetHistories: (state) => {
            return state.latest_question_set_histories
        },
        questionSetIds: (state) => {
            return state.question_set_ids
        },
        currentMission: (state) => {
            return state.current_Mission
        },
        carouselSubject: (state) => {
            return state.carousel_subject
        },
        questionSetProgress: (state) => {
            return state.question_set_progresses
        },
        cardId: (state) => {
            return state.card_Id
        }
    },
    // actions
    actions: {
        async fetchMissionList() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/mission/list',
                };
                const missions = await apiRequest(config);
                this.mission_list = missions.data
                versionStore.saveAppVersion(missions.headers["x-app-version"])
                return missions.data
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchGenerateMissionList(payload: any) {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/mission/generate',
                    data: payload,
                };
                const generator = await apiRequest(config);
                this.mission_list = generator.data
                versionStore.saveAppVersion(generator.headers["x-app-version"])
                return generator.data
            }
            catch (e) {
                redirect_error_page(e)
                return null // NFR-5288 エラーが発生した場合、nullを返す
            }
        },
        async fetchRecommendedSubjects(){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/work_progress/recommended_subject_fields',
                };
                const recommend = await apiRequest(config);
                this.recommend_list = recommend.data.recommended_subject_fields
                versionStore.saveAppVersion(recommend.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchLatestQuestionSetHistories(payload: any){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/latest_question_set_histories',
                    data: payload,
                };
                const latest_qsh = await apiRequest(config);
                this.latest_question_set_histories = latest_qsh.data.question_set_histories
                versionStore.saveAppVersion(latest_qsh.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchMissionQuestionSetProgress(payload) {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/work_progress/question_set_progress',
                    data: payload,
                };
                const qs_progresses = await apiRequest(config);
                this.question_set_progresses = qs_progresses.data.question_set_progresses
                versionStore.saveAppVersion(qs_progresses.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        saveQuestionSetIds(payload: any) {
            this.question_set_ids = payload
        },
        saveCurrentMission(payload: string) {
            this.current_Mission = payload
        },
        saveCarouselSubject(payload: any) {
            this.carousel_subject = payload
        },
        saveCardId(payload:string) {
            this.card_Id = payload
        }
    }
})

export {useTopStore, IMission, IQuestionSetProgresses, ILatestQuestionSetHistories, IRecommendedSubject}